import React, { useState, useEffect } from 'react';
import { Users, Eye, Send, Grid3X3 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import StatusBadge from './shared/StatusBadge';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const SubJobsSection = ({ parentJobId, onSubmitCandidate }) => {
  const [subJobs, setSubJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (parentJobId) {
      fetchSubJobs();
    }
  }, [parentJobId]);

  const fetchSubJobs = async () => {
    try {
      setLoading(true);
      console.log('[SubJobsSection] Fetching sub-jobs for parent:', parentJobId);
      
      // Use dedicated endpoint for sub-jobs instead of fetching all jobs
      const response = await fetch(`${BACKEND_URL}/api/jobs/${parentJobId}/sub-jobs`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const subJobsArray = data.sub_jobs || [];
      
      console.log('[SubJobsSection] Fetched', subJobsArray.length, 'sub-jobs');
      
      // Sort by sub_job_index
      subJobsArray.sort((a, b) => (a.sub_job_index || 0) - (b.sub_job_index || 0));
      
      setSubJobs(subJobsArray);
    } catch (error) {
      console.error('[SubJobsSection] Error fetching sub-jobs:', error);
      setSubJobs([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Grid3X3 className="h-5 w-5" />
          Sub-Jobs
        </h3>
        <p className="text-muted-foreground">Loading sub-jobs...</p>
      </div>
    );
  }

  if (subJobs.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Grid3X3 className="h-5 w-5" />
          Sub-Jobs ({subJobs.length} positions)
        </h3>
        <Badge variant="outline" className="text-xs">
          Individual Tracking
        </Badge>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        This job has multiple openings. Each sub-job can be tracked independently for candidate submissions.
      </p>

      <div className="space-y-3">
        {subJobs.map((subJob, index) => (
          <div
            key={subJob.id}
            className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
            data-testid={`sub-job-${index}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-base">
                    {subJob.title}
                  </h4>
                  <Badge variant="secondary" className="text-xs">
                    {subJob.sub_job_index} of {subJob.total_sub_jobs}
                  </Badge>
                  <StatusBadge status={subJob.status} size="sm" />
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Grid3X3 className="h-4 w-4" />
                    Job ID: {subJob.id.substring(0, 8)}...
                  </span>
                  {subJob.hiring_manager && (
                    <span>Hiring Manager: {subJob.hiring_manager}</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/jobs/${subJob.id}`)}
                  data-testid={`view-sub-job-${index}`}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => onSubmitCandidate && onSubmitCandidate(subJob)}
                  data-testid={`submit-to-sub-job-${index}`}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Submit Candidate
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubJobsSection;
